import { Module } from 'vuex'
import { RouteRecordRaw } from 'vue-router'
import router, { constantRoutes, dynamicRoutes } from '@/router'
import { getRouters } from '@/service/main/system/menu'
import Layout from '@/layout/index.vue'
import ParentView from '@/components/ParentView/index.vue'
import InnerLink from '@/layout/components/InnerLink/index.vue'
import auth from '@/plugins/auth'
import { IDataType } from '@/service/types'

export interface PermissionState {
  routes: RouteRecordRaw[]
  addRoutes: RouteRecordRaw[]
  defaultRoutes: RouteRecordRaw[]
  topbarRouters: RouteRecordRaw[]
  sidebarRouters: RouteRecordRaw[]
}

const state: PermissionState = {
  routes: [],
  addRoutes: [],
  defaultRoutes: [],
  topbarRouters: [],
  sidebarRouters: []
}

const mutations = {
  SET_ROUTES(state: PermissionState, routes: RouteRecordRaw[]) {
    state.addRoutes = routes
    state.routes = constantRoutes.concat(routes)
  },
  SET_DEFAULT_ROUTES(state: PermissionState, routes: RouteRecordRaw[]) {
    state.defaultRoutes = constantRoutes.concat(routes)
  },
  SET_TOPBAR_ROUTES(state: PermissionState, routes: RouteRecordRaw[]) {
    state.topbarRouters = routes
  },
  SET_SIDEBAR_ROUTERS(state: PermissionState, routes: RouteRecordRaw[]) {
    state.sidebarRouters = routes
  }
}

const actions = {
  // 生成路由
  async GenerateRoutes({ commit }: any) {
    // 向后端请求路由数据
    const res: IDataType<any> = await getRouters()
    console.log('res',res)
    const sdata = JSON.parse(JSON.stringify(res.data))
    const rdata = JSON.parse(JSON.stringify(res.data))
    const sidebarRoutes = filterAsyncRouter(sdata)
    const rewriteRoutes = filterAsyncRouter(rdata, false, true)
    const asyncRoutes = filterDynamicRoutes(dynamicRoutes)
    // 先添加从后端获取的路由
    rewriteRoutes.forEach((route) => {
      console.log('添加后端路由:', route.path)
      router.addRoute(route)
    })
    // 再添加动态路由
    asyncRoutes.forEach((route) => {
      console.log('添加动态路由:', route.path)
      router.addRoute(route)
    })
    // 最后添加404路由，确保它能捕获所有未匹配的路径
    router.addRoute({ path: '/:pathMatch(.*)*', redirect: '/404', hidden: true } as any)
    console.log('动态路由加载完成，当前路由表:', router.getRoutes())
    commit('SET_ROUTES', rewriteRoutes)
    commit('SET_SIDEBAR_ROUTERS', constantRoutes.concat(sidebarRoutes))
    commit('SET_DEFAULT_ROUTES', sidebarRoutes)
    commit('SET_TOPBAR_ROUTES', sidebarRoutes)
    return rewriteRoutes
  }
}

// 遍历后台传来的路由字符串，转换为组件对象
type AsyncRoute = any
function filterAsyncRouter(asyncRouterMap: AsyncRoute[], lastRouter = false, type = false): AsyncRoute[] {
  return asyncRouterMap.filter(route => {
    // 检查路径是否为外部链接（以http或https开头）
    const isExternal = /^(https?:|mailto:|tel:)/.test(route.path)
    
    // 如果是外部链接，修改路径以符合Vue Router要求
    if (isExternal) {
      // 保存原始链接到meta.link
      route.meta = route.meta || {}
      route.meta.link = route.path
      // 修改路径为以/开头的路径，用于Vue Router
      route.path = '/redirect-to-external/' + encodeURIComponent(route.path)
    }
    
    // 处理通配符路由，将 path: '*' 转换为 path: '/:pathMatch(.*)*'
    if (route.path === '*') {
      route.path = '/:pathMatch(.*)*'
    }
    
    if (type && route.children) {
        route.children = filterChildren(route.children, undefined)
      }
    if (route.component) {
      // Layout ParentView 组件特殊处理
      if (route.component === 'Layout') {
        route.component = Layout
      } else if (route.component === 'ParentView') {
        route.component = ParentView
      } else if (route.component === 'InnerLink') {
        route.component = InnerLink
      } else {
        route.component = loadView(route.component)

      }
    }
    if (route.children != null && route.children && route.children.length) {
      route.children = filterAsyncRouter(route.children, route, type)
    } else {
      delete route['children']
      delete route['redirect']
    }
    return true
  })
}

function filterChildren(childrenMap: AsyncRoute[], lastRouter: any): AsyncRoute[] {
  let children: AsyncRoute[] = []
  childrenMap.forEach((el, index) => {
    if (el.children && el.children.length) {
      if (el.component === 'ParentView' && !lastRouter) {
        el.children.forEach((c: any) => {
          c.path = el.path + '/' + c.path
          if (c.children && c.children.length) {
            children = children.concat(filterChildren(c.children, c))
            return
          }
          children.push(c)
        })
        return
      }
    }
    if (lastRouter) {
      el.path = lastRouter.path + '/' + el.path
      if (el.children && el.children.length) {
        children = children.concat(filterChildren(el.children, el))
        return
      }
    }
    children = children.concat(el)
  })
  return children
}

// 动态路由遍历，验证是否具备权限
export function filterDynamicRoutes(routes: AsyncRoute[]): AsyncRoute[] {
  const res: AsyncRoute[] = []
  routes.forEach(route => {
    console.log('route',route,auth)
    
    // 检查权限
    let hasPermission = true
    if (route.permissions) {
      hasPermission = auth.hasPermiOr(route.permissions)
    } else if (route.roles) {
      hasPermission = auth.hasRoleOr(route.roles)
    }
    
    // 只有有权限的路由才添加
    if (hasPermission) {
      res.push(route)
    }
  })
  return res
}

export const loadView = (view: string) => {
  if (process.env.NODE_ENV === 'development') {
    return () => import(`@/views/${view}.vue`)
  } else {
    // 使用 import 实现生产环境的路由懒加载
    return () => import(`@/views/${view}`)
  }
}

const permission: Module<PermissionState, any> = {
  namespaced: true,
  state,
  mutations,
  actions
}

export default permission
